/**
 * Menu hooks.
 *
 * The menu is fetched whole and cached: a terminal opening a table should not
 * make forty requests while a guest waits. Mutations invalidate the one key, so
 * an 86 lands everywhere on the screen at once rather than in whichever panel
 * happened to refetch.
 *
 * Prices arrive as strings. That is deliberate on both sides — the API sends
 * decimals rather than floats so nothing rounds in transit, and this module
 * never converts them to `number` for arithmetic. Formatting is display only.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAttemptKeys } from './attempt';
import type { ApiError } from './client';
import { useApi } from './provider';

export type UnavailableReason =
  'inactive' | '86ed' | 'out_of_hours' | 'inactive_at_location' | '86ed_at_location';

export interface MenuCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  /** The category it sits inside ("Drinks" for "Juices"), or null at the top. */
  parent_id: string | null;
}

/** A category at the top level and the categories inside it, in till order. */
export interface CategoryGroup {
  category: MenuCategory;
  inside: MenuCategory[];
}

/**
 * The categories as the till and the menu page show them: each top-level one
 * in its order, with the ones inside it in theirs. One level only, as the
 * server keeps it; one whose group has gone is shown at the top rather than
 * lost.
 */
export function groupCategories(categories: readonly MenuCategory[]): CategoryGroup[] {
  const ordered = [...categories].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  );
  // `!parent_id` rather than `=== null`: a menu an offline till saved before
  // categories had groups has no `parent_id` at all.
  const tops = new Set(ordered.filter((c) => !c.parent_id).map((c) => c.id));
  const groups = ordered
    .filter((c) => !c.parent_id || !tops.has(c.parent_id))
    .map((category) => ({ category, inside: [] as MenuCategory[] }));
  const byId = new Map(groups.map((group) => [group.category.id, group]));
  for (const category of ordered) {
    if (category.parent_id && tops.has(category.parent_id)) {
      byId.get(category.parent_id)?.inside.push(category);
    }
  }
  return groups;
}

/** "Drinks › Juices" for a category inside another; its own name otherwise. */
export function categoryPath(categories: readonly MenuCategory[], id: string): string {
  const category = categories.find((c) => c.id === id);
  if (!category) return '';
  const parent = category.parent_id
    ? categories.find((c) => c.id === category.parent_id)
    : undefined;
  return parent ? `${parent.name} › ${category.name}` : category.name;
}

export interface MenuVariant {
  id: string;
  name: string;
  price_delta: string;
  is_default: boolean;
}

export interface ModifierOption {
  id: string;
  name: string;
  price_delta: string;
  is_available: boolean;
  /** False once retired. Only the modifier-groups list carries retired options. */
  is_active?: boolean;
  /**
   * Set when the modifier is itself something the kitchen consumes — "extra
   * cheese" is 30g out of the walk-in — and points at the menu item carrying
   * its recipe. Stock comes off the shelf for these when the tab closes.
   *
   * Null on the ordinary kind. "No onions" and "well done" are instructions
   * rather than goods, and nothing moves for them. Worth surfacing on a menu
   * admin screen: a modifier that should deplete and doesn't is invisible
   * until it turns up as shrinkage on a count weeks later.
   */
  linked_item_id: string | null;
}

export interface ModifierGroup {
  id: string;
  name: string;
  prompt: string | null;
  min_select: number;
  max_select: number | null;
  options: ModifierOption[];
  /** False once retired: no longer asked on any dish. */
  is_active?: boolean;
}

export interface MenuItem {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  sku: string | null;
  price: string;
  price_source: 'base' | 'price_book' | 'location_override';
  tax_class: string;
  /**
   * The class it sells under when taken away, where that differs — a
   * sandwich standard-rated at a table and zero-rated in a bag. Null means the
   * same class either way. Absent from a server older than it.
   */
  takeaway_tax_class?: string | null;
  prep_station: string | null;
  sort_order: number;
  is_active: boolean;
  is_available: boolean;
  unavailable_reasons: UnavailableReason[];
  eightysix_reason: string | null;
  variants: MenuVariant[];
  modifier_groups: ModifierGroup[];
  /**
   * The dish's photo in three sizes, or null. Signed URLs: they load in an
   * <img> without the session, and expire within a day or so, which is why
   * they are taken fresh from the menu rather than stored.
   */
  image: ImageUrls | null;
}

export interface ImageUrls {
  thumb: string;
  medium: string;
  large: string;
}

export interface Menu {
  location_id: string | null;
  channel: string;
  resolved_at: string;
  categories: MenuCategory[];
  items: MenuItem[];
}

export interface PriceQuote {
  item_id: string;
  variant_id: string | null;
  unit_price: string;
  base_component: string;
  modifier_component: string;
  source: string;
  price_book_id: string | null;
}

export interface MenuQueryOptions {
  locationId?: string | undefined;
  channel?: string | undefined;
  includeUnavailable?: boolean | undefined;
  /** False skips the request: for a screen shown to people who cannot read the menu. */
  enabled?: boolean | undefined;
}

export function useMenu(options: MenuQueryOptions = {}) {
  const api = useApi();
  const {
    locationId,
    channel = 'dine_in',
    includeUnavailable = true,
    enabled = true,
  } = options;

  const search = new URLSearchParams({ channel });
  if (locationId) search.set('location_id', locationId);
  if (!includeUnavailable) search.set('include_unavailable', 'false');

  return useQuery({
    queryKey: ['menu', locationId ?? null, channel, includeUnavailable],
    queryFn: ({ signal }) => api.get<Menu>(`/v1/menu?${search.toString()}`, signal),
    enabled,
    // A menu changes when someone edits it, not between renders. Long enough to
    // stay off the hot path; an 86 arrives through invalidation, not polling.
    staleTime: 5 * 60_000,
  });
}

export function useMenuCategories() {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'categories'],
    queryFn: ({ signal }) => api.get<MenuCategory[]>('/v1/menu/categories', signal),
  });
}

export function useModifierGroups() {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'modifier-groups'],
    queryFn: ({ signal }) => api.get<ModifierGroup[]>('/v1/menu/modifier-groups', signal),
    staleTime: 5 * 60_000,
  });
}

export interface CreateCategoryInput {
  name: string;
  description?: string;
  sort_order?: number;
  /** A top-level category to put it inside. */
  parent_id?: string | null;
}

export function useCreateCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MenuCategory, ApiError, CreateCategoryInput>({
    mutationFn: (input) => api.post<MenuCategory>('/v1/menu/categories', input),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/**
 * Rename a category, change its description, or move it into another
 * (`parent_id`) or back to the top (`parent_id: null`). 409 for a name another
 * category has, or a move that would go two levels deep.
 */
export function useUpdateCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    MenuCategory,
    ApiError,
    { id: string; name?: string; description?: string | null; parent_id?: string | null }
  >({
    mutationFn: ({ id, ...body }) =>
      api.request<MenuCategory>(`/v1/menu/categories/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/** Put every category in a new order — the order the till shows them in. */
export function useReorderCategories() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MenuCategory[], ApiError, string[]>({
    mutationFn: (categoryIds) =>
      api.request<MenuCategory[]>('/v1/menu/categories/order', {
        method: 'PUT',
        body: { category_ids: categoryIds },
      }),
    onSettled: () => invalidateMenu(queryClient),
  });
}

/** Take away an empty category. 409, saying how many, while dishes are still in it. */
export function useRemoveCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.request<void>(`/v1/menu/categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

export interface CreateItemInput {
  category_id: string;
  name: string;
  base_price: string;
  description?: string;
  sku?: string;
  prep_station?: string;
  /** "standard" when left out. */
  tax_class?: string;
  /** Professional (`pricing.tax.classes`). */
  takeaway_tax_class?: string | null;
}

export function useCreateItem() {
  const api = useApi();
  const queryClient = useQueryClient();
  const attempts = useAttemptKeys();
  return useMutation<{ id: string }, ApiError, CreateItemInput>({
    mutationFn: (input) =>
      api.post<{ id: string }>('/v1/menu/items', input, {
        idempotencyKey: attempts.keyFor(input),
      }),
    onSuccess: (_item, input) => {
      attempts.succeeded(input);
      invalidateMenu(queryClient);
    },
  });
}

export interface UpdateItemInput {
  id: string;
  /** Move the dish to another category. */
  category_id?: string;
  name?: string;
  description?: string;
  base_price?: string;
  prep_station?: string;
  is_active?: boolean;
  /** An uploaded image's id, or null to remove the photo. Omit to leave it. */
  image_file_id?: string | null;
  /** Reaches dishes rung in from now on; bills already open keep theirs. */
  tax_class?: string;
  /**
   * Null for the same class either way; omit to leave it. Naming one is
   * Professional (`pricing.tax.classes`, 402 below it).
   */
  takeaway_tax_class?: string | null;
}

export function useUpdateItem() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, UpdateItemInput>({
    mutationFn: ({ id, ...changes }) =>
      api.request<void>(`/v1/menu/items/${id}`, { method: 'PATCH', body: changes }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/**
 * Mark an item off the menu, or put it back.
 *
 * Optimistic on purpose. 86-ing happens mid-service with a queue at the pass,
 * and waiting for a round trip before the button responds is how a manager taps
 * it three times. The server call is idempotent, so those three taps are still
 * one 86.
 */
export function useSetEightySix() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { itemId: string; off: boolean; reason?: string }>({
    mutationFn: ({ itemId, off, reason }) =>
      off
        ? api.post<void>(`/v1/menu/items/${itemId}/eighty-six`, { reason: reason ?? null })
        : api.request<void>(`/v1/menu/items/${itemId}/eighty-six`, { method: 'DELETE' }),

    onMutate: async ({ itemId, off, reason }) => {
      await queryClient.cancelQueries({ queryKey: ['menu'] });
      const snapshots = queryClient.getQueriesData<Menu>({ queryKey: ['menu'] });

      for (const [key, menu] of snapshots) {
        if (!menu || !Array.isArray(menu.items)) continue;
        queryClient.setQueryData<Menu>(key, {
          ...menu,
          items: menu.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  is_available: !off && item.unavailable_reasons.length <= 1,
                  unavailable_reasons: off ? (['86ed'] as UnavailableReason[]) : [],
                  eightysix_reason: off ? (reason ?? null) : null,
                }
              : item,
          ),
        });
      }
      return { snapshots };
    },

    onError: (_error, _vars, context) => {
      // Put the server's version back. A stale optimistic 86 left on screen
      // would have staff telling guests an item is off when it is not.
      const ctx = context as
        { snapshots?: [readonly unknown[], Menu | undefined][] } | undefined;
      for (const [key, menu] of ctx?.snapshots ?? []) {
        queryClient.setQueryData(key, menu);
      }
    },

    onSettled: () => invalidateMenu(queryClient),
  });
}

export interface PriceQuoteInput {
  item_id: string;
  variant_id?: string | null;
  modifier_option_ids?: string[];
  location_id?: string | null;
  channel?: string;
}

/**
 * Ask the server what a line would cost.
 *
 * The POS calls this while a modifier sheet is open, so the total on screen is
 * the total that gets written. Pricing is never recomputed in the browser: the
 * rules live in one place, and a second implementation here would eventually
 * disagree with the first in front of a guest.
 */
export interface AvailabilityWindow {
  id: string;
  item_id: string;
  /** 0 is Monday, 6 is Sunday. Null means every day. */
  day_of_week: number | null;
  starts_at: string;
  ends_at: string;
}

export interface LocationOverride {
  id: string;
  item_id: string;
  location_id: string;
  /** Null means the group price with this site's availability — not free. */
  price: string | null;
  is_active: boolean;
  is_86ed: boolean;
}

/** Monday first, as a rota is written. */
export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export function useAvailabilityWindows(itemId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'availability', itemId],
    queryFn: ({ signal }) =>
      api.get<AvailabilityWindow[]>(`/v1/menu/items/${itemId}/availability`, signal),
    enabled: Boolean(itemId),
  });
}

/**
 * Breakfast until 11:30.
 *
 * An end before the start wraps midnight, so a late menu running 22:00 to
 * 02:00 is one window rather than two.
 */
export function useAddAvailabilityWindow() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    AvailabilityWindow,
    ApiError,
    { itemId: string; startsAt: string; endsAt: string; dayOfWeek: number | null }
  >({
    mutationFn: ({ itemId, startsAt, endsAt, dayOfWeek }) =>
      api.post<AvailabilityWindow>(`/v1/menu/items/${itemId}/availability`, {
        starts_at: startsAt,
        ends_at: endsAt,
        day_of_week: dayOfWeek,
      }),
    onSettled: () => invalidateMenu(queryClient),
  });
}

/**
 * An item with no windows left is unscheduled, not permanently closed — which
 * is why removing the last one is an ordinary thing to do rather than a
 * warning.
 */
export function useRemoveAvailabilityWindow() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { itemId: string; windowId: string }>({
    mutationFn: ({ itemId, windowId }) =>
      api.request<void>(`/v1/menu/items/${itemId}/availability/${windowId}`, {
        method: 'DELETE',
      }),
    onSettled: () => invalidateMenu(queryClient),
  });
}

/** Let one site differ from the group menu. */
export function useSetLocationOverride() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    LocationOverride,
    ApiError,
    {
      itemId: string;
      locationId: string;
      price?: string | null;
      isActive?: boolean;
      is86ed?: boolean;
    }
  >({
    mutationFn: ({ itemId, locationId, price, isActive, is86ed }) =>
      api.request<LocationOverride>(`/v1/menu/items/${itemId}/location-override`, {
        method: 'PUT',
        body: {
          location_id: locationId,
          price: price ?? null,
          is_active: isActive ?? true,
          is_86ed: is86ed ?? false,
        },
      }),
    onSettled: () => invalidateMenu(queryClient),
  });
}

export function usePriceQuote() {
  const api = useApi();
  return useMutation<PriceQuote, ApiError, PriceQuoteInput>({
    mutationFn: (input) => api.post<PriceQuote>('/v1/menu/price-quote', input),
  });
}

function invalidateMenu(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['menu'] });
}

/**
 * How this terminal writes money and dates: the site's currency and locale.
 *
 * Set once by each app as soon as it knows which site it is showing, and read
 * by `formatMoney` and `displayLocale`. A module value rather than a context,
 * because `formatMoney` is called from ~150 places, many of them plain
 * functions, and threading a currency through each is how one gets missed —
 * which is how a restaurant in Lyon saw pounds on its till.
 *
 * Starts as GBP / en-GB only because that is what existing rows hold; an app
 * sets it before anything is shown.
 */
let display = { currency: 'GBP', locale: 'en-GB' };

export function setDisplayFormat(next: { currency: string; locale: string }): void {
  display = { currency: next.currency, locale: next.locale };
}

/** The site's locale, for dates and numbers that are not money. */
export function displayLocale(): string {
  return display.locale;
}

/** Format a decimal string for display. Never parse these back for arithmetic. */
export function formatMoney(
  amount: string,
  currency: string = display.currency,
  locale: string = display.locale,
): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

const REASON_TEXT: Record<UnavailableReason, string> = {
  '86ed': 'Off the menu',
  '86ed_at_location': 'Off at this site',
  inactive: 'Not on the menu',
  inactive_at_location: 'Not at this site',
  out_of_hours: 'Outside its hours',
};

/** Say why in words a waiter would use, not in the API's vocabulary. */
export function describeUnavailable(reasons: UnavailableReason[]): string {
  if (reasons.length === 0) return '';
  return reasons.map((reason) => REASON_TEXT[reason] ?? reason).join(' · ');
}

/**
 * Format a limit for display.
 *
 * Limits arrive as decimals because the API sends every numeric as one — so a
 * cap of 300 reaches the browser as "300.00" and reads as a price rather than a
 * count. `null` is unlimited, and saying so beats printing a number nobody set.
 */
export function formatLimit(cap: number | null | undefined): string {
  if (cap === null || cap === undefined) return 'unlimited';
  return new Intl.NumberFormat().format(Math.trunc(cap));
}

/**
 * A meal deal: several items sold together for less than their sum.
 *
 * **Detected, not rung in.** There is no combo button — an order is scanned as
 * lines are added and a set that matches gets the price. So a POS does not
 * apply these; it shows that they applied. `discount_total` on the order is
 * where the saving appears.
 */
export interface Combo {
  id: string;
  code: string;
  name: string;
  /** What the whole set sells for. */
  price: string;
  is_active: boolean;
  components: ComboComponent[];
}

export interface ComboComponent {
  item_id: string;
  item_name: string;
  quantity: number;
}

/**
 * The meal deals in force.
 *
 * A till does not need this to price an order. A menu screen does, and so does
 * anybody asking why a bill came to less than the sum of its parts — which is
 * the question a deal generates at the counter about once a service.
 */
export function useCombos(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'combos', includeInactive],
    queryFn: ({ signal }) =>
      api.get<Combo[]>(`/v1/menu/combos?include_inactive=${includeInactive}`, signal),
    staleTime: 60_000,
  });
}

/**
 * Define a meal deal. Professional and above.
 *
 * It applies from the moment it is saved — the next order containing the
 * components is priced with it. Worth saying so on the form: there is no
 * "publish" step and no till configuration to push.
 *
 * At least two components. One item at a price is a price book entry, and the
 * server refuses a single-component combo saying so.
 *
 * A deal priced above its parts is accepted and never applies. That is
 * deliberate — a misconfigured deal must not make a bill more expensive — but
 * it means a form can save something that does nothing, so it is worth
 * comparing the price against the components' total and warning before saving.
 */
export function useCreateCombo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Combo,
    ApiError,
    {
      code: string;
      name: string;
      price: string;
      components: { itemId: string; quantity?: number }[];
    }
  >({
    mutationFn: (input) =>
      api.post<Combo>('/v1/menu/combos', {
        code: input.code,
        name: input.name,
        price: input.price,
        components: input.components.map((c) => ({
          item_id: c.itemId,
          quantity: c.quantity ?? 1,
        })),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

/**
 * A price book: a set of prices that wins over the base menu while it applies.
 *
 * Happy hour, a delivery channel, a Christmas menu. Higher `priority` wins, and
 * priority is unique per channel — so the order books apply in is decided by
 * whoever created them rather than discovered from whichever row came back
 * first.
 */
export interface PriceBook {
  id: string;
  name: string;
  /** `dine_in`, `delivery`, `takeaway`. A book applies to one channel. */
  channel: string;
  /** Higher wins. Unique per channel — the server refuses a clash. */
  priority: number;
  /**
   * When the book applies. **Null means always**, which is not the same as a
   * window that has passed, and the two must read differently — a book with no
   * window is on right now, and one whose window ended is not.
   */
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  /**
   * How many prices this book overrides.
   *
   * The number that answers "did that save". An empty price book and a full
   * one are indistinguishable without it, and an empty one silently changes
   * nothing — which looks exactly like a bug in pricing.
   */
  entries: number;
}

export interface PriceBookEntry {
  id: string;
  item_id: string;
  item_name: string;
  /** Null means the price applies to every size of the item. */
  variant_id: string | null;
  price: string;
}

/** The price books, whether or not they are currently in their window. */
export function usePriceBooks() {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'price-books'],
    queryFn: ({ signal }) => api.get<PriceBook[]>('/v1/menu/price-books', signal),
    staleTime: 60_000,
  });
}

/** What one book prices, and at what. */
export function usePriceBookEntries(priceBookId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['menu', 'price-books', priceBookId ?? null, 'entries'],
    queryFn: ({ signal }) =>
      api.get<PriceBookEntry[]>(`/v1/menu/price-books/${priceBookId}/entries`, signal),
    enabled: Boolean(priceBookId),
    staleTime: 30_000,
  });
}

/**
 * Open a price book. Professional and above.
 *
 * `priority` is unique per channel and the server refuses a clash by name —
 * "Happy hour already has priority 10 on dine_in" — so show that message
 * rather than a generic failure; it tells somebody exactly what to change.
 *
 * Leaving the window off means the book is always on. Worth making that an
 * explicit choice on the form: a book created with no window starts changing
 * prices immediately, which is right when intended and a surprise when not.
 */
export function useCreatePriceBook() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PriceBook,
    ApiError,
    {
      name: string;
      channel?: string;
      priority?: number;
      validFrom?: string;
      validTo?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<PriceBook>('/v1/menu/price-books', {
        name: input.name,
        channel: input.channel ?? 'dine_in',
        priority: input.priority ?? 0,
        valid_from: input.validFrom ?? null,
        valid_to: input.validTo ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

/**
 * Price one dish in one book.
 *
 * A PUT because it is idempotent — setting the same item twice replaces rather
 * than adding, so a form that saves on blur cannot create duplicates.
 *
 * Omitting `variantId` reprices **every size** of the item, which is how a
 * whole dish goes on offer without enumerating them. Say so on the form: it is
 * the difference between discounting a large and discounting all three.
 */
export function useSetPriceBookEntry() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PriceBookEntry,
    ApiError,
    { priceBookId: string; itemId: string; price: string; variantId?: string }
  >({
    mutationFn: ({ priceBookId, itemId, price, variantId }) =>
      // `body` is serialised by `request`, so it takes the object. Passing a
      // string here encoded it twice and the server saw a JSON string where a
      // body should be — every attempt to price a dish came back 422.
      api.request<PriceBookEntry>(`/v1/menu/price-books/${priceBookId}/entries`, {
        method: 'PUT',
        body: {
          item_id: itemId,
          price,
          variant_id: variantId ?? null,
        },
      }),
    onSettled: () => {
      // The whole menu, not just the books. Pricing a dish in an active book
      // changes what a guest is charged, and `['menu', 'price-books']` does
      // not match `['menu', locationId, channel, ...]` — so every menu screen
      // open anywhere went on showing the old price until its staleTime ran
      // out. `useCreatePriceBook` already invalidates the wider key.
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

/**
 * Change a price book after it is open: withdraw it, bring it back, rename it,
 * or move its window.
 *
 * `is_active` is the off switch. `PriceBook.is_active` was read on every quote
 * and written by nothing, so a book that had started repricing the menu could
 * not be stopped — which made a window the only brake there was, and a window
 * could only be set at creation.
 *
 * **`clearWindow` rather than a null `validTo`.** "This book has no end date"
 * and "leave the end date alone" are different instructions and one nullable
 * field cannot carry both: sending `valid_to: null` to mean the second would
 * silently do the first. So an explicit flag says which is meant.
 */
export function useUpdatePriceBook() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PriceBook,
    ApiError,
    {
      priceBookId: string;
      name?: string;
      isActive?: boolean;
      validFrom?: string;
      validTo?: string;
      clearWindow?: boolean;
    }
  >({
    mutationFn: ({ priceBookId, name, isActive, validFrom, validTo, clearWindow }) =>
      api.request<PriceBook>(`/v1/menu/price-books/${priceBookId}`, {
        method: 'PATCH',
        body: {
          ...(name === undefined ? {} : { name }),
          ...(isActive === undefined ? {} : { is_active: isActive }),
          ...(validFrom === undefined ? {} : { valid_from: validFrom }),
          ...(validTo === undefined ? {} : { valid_to: validTo }),
          ...(clearWindow ? { clear_window: true } : {}),
        },
      }),
    onSettled: () => {
      // Withdrawing a book changes what a guest is charged, so the menu goes
      // as stale as the book list does.
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

/**
 * Take a dish back out of a book.
 *
 * The nearest thing before this existed was setting the price back to whatever
 * the menu said, which is not the same act: an entry at the base price is
 * still an override, and it goes on overriding after somebody changes the base
 * price.
 */
export function useRemovePriceBookEntry() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { priceBookId: string; entryId: string }>({
    mutationFn: ({ priceBookId, entryId }) =>
      api.request<void>(`/v1/menu/price-books/${priceBookId}/entries/${entryId}`, {
        method: 'DELETE',
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

/**
 * Withdraw a meal deal, or bring one back.
 *
 * `Combo.is_active` was read when an order was priced and written by nothing,
 * so a deal saved by mistake applied to every matching order from then on with
 * no way to stop it.
 */
export function useUpdateCombo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Combo, ApiError, { comboId: string; isActive: boolean }>({
    mutationFn: ({ comboId, isActive }) =>
      api.request<Combo>(`/v1/menu/combos/${comboId}`, {
        method: 'PATCH',
        body: { is_active: isActive },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });
}

export interface UploadedImage {
  id: string;
  width: number;
  height: number;
  urls: ImageUrls;
}

/**
 * Upload a photo: JPEG, PNG or WebP up to 10 MB.
 *
 * The server checks it is really an image, turns it upright, strips its
 * metadata (a phone photo carries where it was taken) and keeps three sizes.
 * Attach it with `useUpdateItem({ id, image_file_id })`. 409 `field: "file"`
 * with the reason for anything unusable.
 */
export function useUploadImage() {
  const api = useApi();
  return useMutation<UploadedImage, ApiError, { file: File }>({
    mutationFn: ({ file }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('purpose', 'menu_photo');
      return api.upload<UploadedImage>('/v1/files/images', form);
    },
  });
}

/**
 * Sizes and options: the two things a menu row counted and no screen could set.
 *
 * Both have been in the API since the menu was, and the back office only ever
 * showed "2 sizes" and "3 options" — which a restaurant could not have created
 * in the first place. The endpoints only add: there is no way yet to rename a
 * size, reprice an option, take a group off a dish, or delete either (see
 * `TODO.md`), so the screens over them only add too, and say so.
 */
export interface AddVariantInput {
  itemId: string;
  name: string;
  /** Added to the dish's price. "0" for a size that costs the same. */
  price_delta: string;
  sort_order?: number;
}

export function useAddVariant() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, AddVariantInput>({
    mutationFn: ({ itemId, ...body }) =>
      api.post<{ id: string }>(`/v1/menu/items/${itemId}/variants`, body),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

export interface CreateModifierGroupInput {
  name: string;
  /** What the till asks. "How would you like it cooked?" */
  prompt?: string | null;
  /** 1 or more makes a choice compulsory at the till. */
  min_select: number;
  /** Null: as many as they like. */
  max_select: number | null;
  options: { name: string; price_delta: string; linked_item_id?: string | null }[];
}

/** 402 below Starter's `catalog.modifiers`; 409 on a min/max that cannot be met. */
export function useCreateModifierGroup() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, CreateModifierGroupInput>({
    mutationFn: (input) => api.post<{ id: string }>('/v1/menu/modifier-groups', input),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

export function useAttachModifierGroup() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { itemId: string; group_id: string; sort_order?: number }>(
    {
      mutationFn: ({ itemId, ...body }) =>
        api.post<void>(`/v1/menu/items/${itemId}/modifier-groups`, body),
      onSuccess: () => invalidateMenu(queryClient),
    },
  );
}

/**
 * Change a size after the fact: rename it, reprice it, retire it or bring it
 * back. Nothing is deleted — orders already taken name it — and a line already
 * on a tab keeps the price it was taken at.
 */
export function useUpdateVariant() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    ApiError,
    {
      itemId: string;
      variantId: string;
      name?: string;
      price_delta?: string;
      is_active?: boolean;
    }
  >({
    mutationFn: ({ itemId, variantId, ...body }) =>
      api.request<void>(`/v1/menu/items/${itemId}/variants/${variantId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/**
 * Change what a group asks, how many answers it takes, or retire it. Send only
 * what changes: a `prompt` or `max_select` sent as null is cleared.
 */
export function useUpdateModifierGroup() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    ApiError,
    {
      groupId: string;
      name?: string;
      prompt?: string | null;
      min_select?: number;
      max_select?: number | null;
      is_active?: boolean;
    }
  >({
    mutationFn: ({ groupId, ...body }) =>
      api.request<void>(`/v1/menu/modifier-groups/${groupId}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

export function useAddModifierOption() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    { groupId: string; name: string; price_delta?: string; linked_item_id?: string | null }
  >({
    mutationFn: ({ groupId, ...body }) =>
      api.post<{ id: string }>(`/v1/menu/modifier-groups/${groupId}/options`, body),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/**
 * Rename, reprice, retire or bring back one option. Never deleted: orders name it.
 *
 * `linked_item_id` links it to the menu item whose recipe it takes off a shelf
 * — "oat milk", "side of fries" — or, as `null`, makes it an instruction again.
 * Leave the key out to leave the link alone: `null` is an answer, not a blank.
 * Linking in place is the point. The link used to be create-only, and replacing
 * an option to add one would have cut it off from every tab that already names
 * it.
 */
export function useUpdateModifierOption() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    ApiError,
    {
      groupId: string;
      optionId: string;
      name?: string;
      price_delta?: string;
      is_active?: boolean;
      linked_item_id?: string | null;
    }
  >({
    mutationFn: ({ groupId, optionId, ...body }) =>
      api.request<void>(`/v1/menu/modifier-groups/${groupId}/options/${optionId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}

/** Stop asking a group's question on one dish. The group stays on the others. */
export function useDetachModifierGroup() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { itemId: string; groupId: string }>({
    mutationFn: ({ itemId, groupId }) =>
      api.request<void>(`/v1/menu/items/${itemId}/modifier-groups/${groupId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateMenu(queryClient),
  });
}
